import { Component, OnInit, Input, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { JobsService } from 'core/index';
import { MarsQuestionsService } from '../services/mars-questions.service';

@Component({
  selector: 'lms-mars-questions-create',
  templateUrl: './mars-questions-create.component.html',
  styleUrls: ['./mars-questions-create.component.scss']
})
export class MarsQuestionsCreateComponent implements OnInit {
  @ViewChild('questionsTable', { static: false }) public questionsTable: ElementRef;
  @Output() created = new EventEmitter();
  @Output() cancel = new EventEmitter();
  @Input() selectable: boolean;
  @Input() groupId: any;
  public paramsSubscription: any;
  public selectedQuestions: any[] = [];
  public excludedQuestions = new Set();
  public isAnySelected = false;
  public isHeaderSelectedAll: boolean = false; // Selection of visible and non-visible rows
  public dataSource: TableDatasource;
  validators: any[] = [];
  touched: boolean = false;
  inputHeight: any = 36;
  answerValues: any = [
    { key: '1', value: '1' },
    { key: '2', value: '2' },
    { key: '3', value: '3' }
  ];

  constructor(
    public location: Location,
    public translate: TranslateService,
    public titleService: Title,
    public route: ActivatedRoute,
    public router: Router,
    private jobsService: JobsService,
    private marsQuestionsService: MarsQuestionsService
  ) {}

  ngOnInit() {
    this.createDatasource();
    this.createEmptyRow();
  }

  createDatasource() {
    this.touched = false;
    this.validators = [];
    this.dataSource = new TableDatasource({
      sort: 'question',
      aggregates: ['group'],
      populate: 'mars',
      pristine: false,
      columns: this.getColumns(),
      docs: []
    });
  }

  getColumns() {
    return [
      {
        source: 'question',
        dataType: DataTypes.String,
        mandatory: true,
        label: this.translate.instant('questions.tableHead.question'),
        cellRenderer: this.getCellRenderer('question')
      },
      {
        source: 'answer1',
        dataType: DataTypes.String,
        mandatory: true,
        label: this.translate.instant('questions.tableHead.answer1'),
        cellRenderer: this.getCellRenderer('answer1')
      },
      {
        source: 'answer2',
        dataType: DataTypes.String,
        mandatory: true,
        label: this.translate.instant('questions.tableHead.answer2'),
        cellRenderer: this.getCellRenderer('answer2')
      },
      {
        source: 'answer3',
        dataType: DataTypes.String,
        mandatory: true,
        label: this.translate.instant('questions.tableHead.answer3'),
        cellRenderer: this.getCellRenderer('answer3')
      },
      {
        source: 'correctAnswer',
        style: { width: '130px' },
        mandatory: true,
        label: this.translate.instant('questions.tableHead.answer'),
        cellRenderer: (renderer, value, callback, row) => {
          return renderer`
          <gl-select
          inline
          small
          mandatory
          style="width: 120px"
          .value="${value}"
          .options="${this.answerValues}"
          .errorMessage="${this.validate('correctAnswer', value, row)}"
          @onChange="${(ev) => this.onUpdateRow(ev, 'correctAnswer', row)}">
          </gl-select>
          `;
        }
      }
    ];
  }

  getCellRenderer(field) {
    return (renderer, value, data, row) => {
      return renderer` <gl-textarea inline style="width: 100%; display: block"
          .value="${value}"
          .errorMessage="${this.validate(field, value, row)}"
          .heightBox=${this.inputHeight}
          @onChange="${(ev) => this.onUpdateRow(ev, field, row)}"
        >
        </gl-textarea>
       `;
    };
  }

  selectionHandler(rowData) {
    if (rowData) {
      if (this.excludedQuestions.has(rowData.id)) return false;
      return (
        this.isHeaderSelectedAll ||
        (this.selectedQuestions && this.selectedQuestions.some((question) => question.id === rowData.id))
      );
    }
    return this.isHeaderSelectedAll;
  }

  onSelectionChanged(selection) {
    const { selected = [] } = selection || {};
    this.selectedQuestions = selected;
    this.isAnySelected = this.selectedQuestions && this.selectedQuestions.length ? true : false;
  }

  setSelectHeader(flag) {
    this.questionsTable.nativeElement.setAllSelected(flag);
  }

  setSelectAll(flag) {
    this.dataSource.selectionHandler = flag ? (data) => this.selectionHandler(data) : null;
    this.excludedQuestions.clear();
    this.selectedQuestions = flag ? this.dataSource.docs : [];
    this.isAnySelected = flag;
    this.isHeaderSelectedAll = flag;
    this.questionsTable.nativeElement.setAllSelected(flag);
  }

  addRow(data: any, dirty?: boolean) {
    this.validators.push({ valid: false, touched: new Set(dirty ? Object.keys(data) : null), errors: {} });
    this.dataSource.docs.push(data);
  }

  createEmptyRow() {
    this.touched = true;
    this.selectable = true;
    this.dataSource.refresh();
    this.addRow({});
  }

  deleteRow(question) {
    const row = this.dataSource.docs.findIndex((doc) => doc === question);
    this.touched = true;
    this.validators.splice(row, 1);
    this.dataSource.docs.splice(row, 1);
    this.selectable = this.dataSource.docs.length ? true : false;
    this.dataSource.refresh();
  }

  deleteBulk(selectedQuestions) {
    selectedQuestions.forEach((question) => this.deleteRow(question));
    this.setSelectAll(false);
    this.dataSource.refresh();
  }

  onUpdateRow(event, field, row) {
    this._setEmptyRow(row);
    this.touched = true;
    this.validators[row].touched.add(field);
    // important to create new object to re-evaluate complete row
    const newData = { ...this.dataSource.docs[row] };
    newData[field] = event.detail.value;
    this.dataSource.docs[row] = newData;
    this.dataSource.refresh();
  }

  onPaste(event) {
    const clipText = event.clipboardData.getData('Text');
    const data = clipText
      .trim()
      .replaceAll('\r\n', '\n')
      .replaceAll('\r', '\n')
      .split('\n')
      .map((row) => row.split('\t').filter((field) => !!field));

    if (data.length > 1 || data[0].length > 1) {
      this.dataSource.docs = this._deleteEmptyRows(this.dataSource.docs);
      event.preventDefault();
      this.selectable = true;
      data.forEach((row) => {
        const range = {};
        this.dataSource.columns
          .filter((col) => !!col.source && col.visible)
          .forEach((col, index) => (range[col.source] = row[index]));
        this.addRow(range, true);
      });
      this.dataSource.refresh();
    }
  }

  validate(field, value, row) {
    const validator = this.validators[row];
    let errorMessage;
    if (value == null || !`${value}`.trim().length) {
      errorMessage = this.translate.instant('error.required');
    }
    if ((field === 'correctAnswer' && value < '1') || (field === 'correctAnswer' && value > '3')) {
      errorMessage = this.translate.instant('error.required');
    }
    if (errorMessage) {
      validator.errors[field] = errorMessage;
    } else {
      delete validator.errors[field];
    }
    validator.valid = Object.values(validator.errors).length === 0;

    // only return error when field has been touched
    if (validator.touched.has(field)) {
      return errorMessage;
    }
  }

  /**
   * Empty row sets to object when value changed
   * @param rowNum
   * @private
   */
  _setEmptyRow(rowNum) {
    if (this.dataSource.docs[rowNum]?.empty) this.dataSource.docs[rowNum] = {};
  }

  /**
   * Delete empty rows of the table
   * @param playerList
   * @private
   */
  _deleteEmptyRows(questionsList) {
    let numRowDeleted = 0;
    const tempRows = [...questionsList];
    questionsList.map((row, index) => {
      if (row.empty) {
        this.validators.splice(index - numRowDeleted, 1);
        tempRows.splice(index - numRowDeleted, 1);
        numRowDeleted++;
      }
    });
    return tempRows;
  }

  get saveDisabled() {
    if (this.touched && this.validators?.length) {
      return this.validators.some((validator) => !validator.valid);
    }
    return true;
  }

  save() {
    const job = this.jobsService.jobTypes.CREATE_QUESTIONS_BULK;
    const data = {
      questions: this.dataSource.docs,
      job
    };

    this.marsQuestionsService.createGroupQuestions(data, this.groupId).then((res: any) => {
      this.created.emit(res.jobId);
    });
  }

  cancelClicked() {
    this.cancel.emit();
  }
}
